import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authToken, setAuthToken] = useState(null);
  const [authCheckInProgress, setAuthCheckInProgress] = useState(false); // Prevent concurrent auth checks

  // Check for existing authentication on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    // Prevent concurrent auth checks
    if (authCheckInProgress) {
      console.log('⏳ Auth check already in progress, skipping...');
      return;
    }
    
    setAuthCheckInProgress(true);
    
    try {
      // First, check for pending OAuth session from callback redirect
      const pendingSession = localStorage.getItem('pending_oauth_session');
      if (pendingSession) {
        console.log('Found pending OAuth session, processing...');
        localStorage.removeItem('pending_oauth_session');
        await handleOAuthCallback(pendingSession);
        setLoading(false);
        setAuthCheckInProgress(false);
        return;
      }

      // Check if we have a session_id in the URL hash (fallback for older flow)
      const hash = window.location.hash;
      if (hash.includes('session_id=')) {
        const sessionId = hash.split('session_id=')[1].split('&')[0];
        await handleOAuthCallback(sessionId);
        // Clean URL
        window.history.replaceState(null, '', window.location.pathname);
        setLoading(false);
        setAuthCheckInProgress(false);
        return;
      }

      // Check for JWT token in localStorage
      const token = localStorage.getItem('authToken');
      const cachedUser = localStorage.getItem('cachedUser');
      
      if (token) {
        setAuthToken(token);
        
        // Immediately set cached user if available to prevent logout flicker
        if (cachedUser) {
          try {
            const parsedUser = JSON.parse(cachedUser);
            console.log('📦 Loading cached user:', parsedUser.email);
            setUser({ ...parsedUser, is_authenticated: true });
          } catch (e) {
            console.error('Failed to parse cached user:', e);
          }
        }
        
        try {
          const response = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true,
            timeout: 10000  // 10 second timeout
          });
          console.log('✅ Auth check with token:', { user: response.data, credits: response.data.credits });
          const freshUser = { ...response.data, is_authenticated: true };
          setUser(freshUser);
          // Cache fresh user data
          localStorage.setItem('cachedUser', JSON.stringify(freshUser));
        } catch (error) {
          console.error('Auth check failed:', error);
          // Only logout if it's a 401/403 (invalid token), not for network errors
          if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            console.log('❌ Token invalid, logging out');
            localStorage.removeItem('authToken');
            localStorage.removeItem('userId');
            localStorage.removeItem('cachedUser');
            setAuthToken(null);
            setUser(null);
          } else {
            console.log('⚠️ Network error during auth check');
            // Keep cached user if available, otherwise keep current state
            if (!user && cachedUser) {
              try {
                const parsedUser = JSON.parse(cachedUser);
                console.log('⚠️ Using cached user due to network error:', parsedUser.email);
                setUser({ ...parsedUser, is_authenticated: true });
              } catch (e) {
                console.error('Failed to use cached user:', e);
              }
            }
          }
        }
      } else {
        // Check for OAuth session cookie
        try {
          const response = await axios.get(`${API_URL}/api/auth/me`, {
            withCredentials: true,
            timeout: 10000
          });
          console.log('✅ Auth check with cookie:', { user: response.data, credits: response.data.credits });
          const freshUser = { ...response.data, is_authenticated: true };
          setUser(freshUser);
          // Cache user data
          localStorage.setItem('cachedUser', JSON.stringify(freshUser));
        } catch (error) {
          // Not authenticated - that's okay for unauthenticated users
          console.log('No existing session');
          // Clear cached user if no auth found
          localStorage.removeItem('cachedUser');
        }
      }
    } catch (error) {
      console.error('Unexpected error in checkAuth:', error);
    } finally {
      setLoading(false);
      setAuthCheckInProgress(false); // Always reset flag
    }
  };

  const handleOAuthCallback = async (sessionId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/auth/session`, {
        headers: { 'X-Session-ID': sessionId },
        withCredentials: true
      });
      
      const userData = response.data;
      
      // Update localStorage with user ID for backward compatibility
      localStorage.setItem('userId', userData.id);
      
      console.log('🔐 Setting user state from OAuth:', userData);
      
      setUser({
        id: userData.id,
        email: userData.email,
        name: userData.name,
        picture: userData.picture,
        credits: userData.credits,
        is_authenticated: true
      });
      
      setLoading(false);
      
      // Force a re-check to ensure cookie-based auth works for subsequent requests
      setTimeout(() => {
        console.log('🔄 Re-checking auth status after OAuth');
        checkAuth();
      }, 500);
      
      return userData;
    } catch (error) {
      console.error('OAuth callback error:', error);
      setLoading(false);
      throw error;
    }
  };

  const register = async (email, password, name, referralCode = null) => {
    try {
      const url = referralCode 
        ? `${API_URL}/api/auth/register?referral_code=${referralCode}`
        : `${API_URL}/api/auth/register`;
        
      const response = await axios.post(url, {
        email,
        password,
        name
      });
      
      const { access_token, user: userData } = response.data;
      
      console.log('📝 Register response:', { userData, credits: userData.credits });
      
      localStorage.setItem('authToken', access_token);
      localStorage.setItem('userId', userData.id);
      setAuthToken(access_token);
      
      // Set user with all fields including credits
      const completeUser = { 
        ...userData, 
        credits: userData.credits || 0,
        is_authenticated: true 
      };
      console.log('✅ Setting user after register:', completeUser);
      setUser(completeUser);
      
      // Refetch to ensure sync
      setTimeout(async () => {
        await checkAuth();
      }, 500);
      
      return userData;
    } catch (error) {
      throw error;
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password
      });
      
      const { access_token, user: userData } = response.data;
      
      console.log('🔐 Login response:', { userData, credits: userData.credits });
      
      localStorage.setItem('authToken', access_token);
      localStorage.setItem('userId', userData.id);
      setAuthToken(access_token);
      
      // Set user with all fields including credits
      const completeUser = { 
        ...userData, 
        credits: userData.credits || 0,
        is_authenticated: true 
      };
      console.log('✅ Setting user after login:', completeUser);
      setUser(completeUser);
      
      // Cache user data for faster loads
      localStorage.setItem('cachedUser', JSON.stringify(completeUser));
      
      // Refetch to ensure sync
      setTimeout(async () => {
        await checkAuth();
      }, 500);
      
      return userData;
    } catch (error) {
      throw error;
    }
  };

  const loginWithGoogle = (onSuccess) => {
    const authUrl = process.env.REACT_APP_AUTH_URL || 'https://auth.emergentagent.com';
    // Use a special OAuth callback URL that will communicate back to parent
    const redirectUrl = `${window.location.origin}/oauth-callback`;
    
    // Open OAuth in a popup window
    const popup = window.open(
      `${authUrl}/?redirect=${encodeURIComponent(redirectUrl)}`,
      'Google Login',
      'width=500,height=600,scrollbars=yes,resizable=yes'
    );
    
    // Listen for message from the popup
    const handleMessage = async (event) => {
      // Security: verify origin
      if (event.origin !== window.location.origin) return;
      
      if (event.data.type === 'OAUTH_SUCCESS') {
        // Remove listener
        window.removeEventListener('message', handleMessage);
        
        // Close popup if still open
        if (popup && !popup.closed) {
          popup.close();
        }
        
        // Handle the OAuth callback with session_id
        try {
          await handleOAuthCallback(event.data.session_id);
          console.log('✅ OAuth login successful');
          // Call success callback if provided
          if (onSuccess) {
            onSuccess();
          }
        } catch (error) {
          console.error('❌ OAuth callback failed:', error);
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    // Also check if popup is closed manually
    const checkPopup = setInterval(() => {
      if (popup && popup.closed) {
        clearInterval(checkPopup);
        window.removeEventListener('message', handleMessage);
        // Check auth status in case callback happened via cookies
        setTimeout(() => {
          checkAuth();
          // Call success callback after checkAuth completes
          if (onSuccess) {
            setTimeout(() => {
              onSuccess();
            }, 1000);
          }
        }, 500);
      }
    }, 500);
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    // Clear all auth-related data
    localStorage.removeItem('authToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('cachedUser');
    setAuthToken(null);
    setUser(null);
  };

  const updateCredits = async () => {
    // Refresh user data to get updated credits
    try {
      console.log('🔄 Updating credits...');
      if (authToken) {
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${authToken}` },
          withCredentials: true
        });
        console.log('✅ Credits updated:', response.data.credits);
        setUser(prevUser => ({
          ...prevUser,
          ...response.data,
          credits: response.data.credits
        }));
      } else {
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          withCredentials: true
        });
        console.log('✅ Credits updated:', response.data.credits);
        setUser(prevUser => ({
          ...prevUser,
          ...response.data,
          credits: response.data.credits
        }));
      }
    } catch (error) {
      console.error('❌ Failed to update credits:', error);
    }
  };
  
  // Immediate optimistic credit deduction for UI responsiveness
  const deductCreditsOptimistic = (amount) => {
    setUser(prevUser => {
      if (!prevUser) return prevUser;
      const newCredits = Math.max(0, prevUser.credits - amount);
      console.log(`⚡ Optimistic deduction: ${prevUser.credits} - ${amount} = ${newCredits}`);
      return {
        ...prevUser,
        credits: newCredits
      };
    });
  };

  const value = {
    user,
    loading,
    authToken,
    register,
    login,
    loginWithGoogle,
    logout,
    updateCredits,
    deductCreditsOptimistic,
    isAuthenticated: !!user,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
